import {IServerSideDatasource, IServerSideGetRowsParams, GridState} from 'ag-grid-community';
import {NumberRPPE} from '@enums/row-per-page.enum';
import {Observable} from 'rxjs';
import {AgGridComponent} from './ag-grid.component';
import {LocalStorageService} from '@services/local-storage.service';
import {AG_GRID_STATES_KEY} from './constants';

export interface IAgDataResponse<RESP> {
  rowData: RESP[];
  rowCount?: number;
}

export abstract class AgGridService<REQ, RESP> {
  abstract get(request: REQ): Observable<IAgDataResponse<RESP>>
}

export class AgDataSource<REQ, RESP> {
  request: REQ;
  get(service: AgGridService<REQ, RESP>): IServerSideDatasource {
    return {
      getRows: (params: IServerSideGetRowsParams) => {
        const direction = params.request.sortModel.find(item => item.sort)?.sort || 'desc';
        const sort = params.request.sortModel.find(item => item.sort)?.colId || 'id';
        let page = params.api.paginationGetCurrentPage();
        let pageSize = params.api.paginationGetPageSize();
        const pagination = params.api.getGridOption('pagination');
        const {startRow, endRow} = params.request;
        if (pagination) {
          params.api.showLoadingOverlay();
        } else {
          // infinite scroll
          //  serialize to server like paging
          pageSize = endRow - startRow;
          page = endRow / pageSize - 1;
        }

        service.get({
          ...this.request,
          rowsPerPage: NumberRPPE[pageSize],
          page,
          sort,
          direction,
          startRow,
          endRow,
        })
        .subscribe({
          next: (data) => {
            params.success(data);
            // side scroll takes width and produces bottom scroll, when strategy is fitGridWidth and
            //  there are rows more than could fit in viewport of grid
            if (params.api.getGridOption('autoSizeStrategy')?.type === 'fitGridWidth') {
              params.api.sizeColumnsToFit();
            }

            if (!data.rowData.length && (pagination || !pagination && page === 0)) {
              // if infinite scroll has no data on first page, show overlay
              params.api.showNoRowsOverlay();
            } else if (pagination) {
              params.api.hideOverlay();
            }
          },
          error: () => params.fail()
        });
      },
    }
  }
}

/**
 * Helper function to convert request before using for batch action in LS
 * todo better to update backend to use selectAll and toggledNodes api
 * @param api
 * @param request
 */
export const prepareSelectionActionRequest = (api: AgGridComponent, request: any): any => {
  const {selectAll, toggledNodes} = api.getSelection();
  const selected = !selectAll && toggledNodes.length ? toggledNodes : [];
  const excluded = selectAll && toggledNodes.length ? toggledNodes : [];

  return {
    ...request,
    selected,
    excluded,
  };
};

export const getGridStates = (localStorageService: LocalStorageService): {[key: string]: GridState} => {
  const states = localStorageService.getValue(AG_GRID_STATES_KEY);
  let statesObj = {};
  try {
    statesObj = JSON.parse(states) || {};
  } catch {
    // no state
  }
  return statesObj;
};

export const getGridStateByKey = (localStorageService: LocalStorageService, stateKey: string): GridState => {
  const states = getGridStates(localStorageService);
  return states[stateKey + localStorageService.getCurrentUsername()] || {};
};

export const setGridState = (localStorageService: LocalStorageService, stateKey: string, state: GridState): void => {
  const states = getGridStates(localStorageService);
  states[stateKey + localStorageService.getCurrentUsername()] = state;
  localStorageService.setValue(AG_GRID_STATES_KEY, JSON.stringify(states));
};